import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useLangPath } from '../../../hooks/useLangPath';
import Typewriter from 'typewriter-effect';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';

// ─── Brand palette ────────────────────────────────────────────────────────────
const C_PURPLE = 0x8258c8;
const C_BLUE   = 0x2c84c8;
const C_TEAL   = 0x45d0bd;

// ─── React Component ──────────────────────────────────────────────────────────
const Hero = () => {
    const { t, i18n } = useTranslation();
    const getPath = useLangPath();
    const isRtl     = i18n.language === 'ar';
    const mountRef  = useRef(null);
    const heroRef   = useRef(null);
    const modelRef  = useRef(null);

    useEffect(() => {
        const container = mountRef.current;
        if (!container) return;

        // ── WebGL support check ───────────────────────────────────────────────
        const testCanvas = document.createElement('canvas');
        const hasWebGL = !!(
            testCanvas.getContext('webgl') ||
            testCanvas.getContext('experimental-webgl')
        );
        if (!hasWebGL) return; // gracefully skip 3D on unsupported environments

        // ── Renderer ──────────────────────────────────────────────────────────
        let renderer;
        try {
            renderer = new THREE.WebGLRenderer({ alpha: true, antialias: false, powerPreference: 'low-power' });
        } catch (e) {
            return;
        }
        renderer.setSize(container.clientWidth, container.clientHeight);
        renderer.setPixelRatio(1); // always 1x — saves significant GPU memory
        renderer.setClearColor(0x000000, 0);
        container.appendChild(renderer.domElement);

        // ── Scene & Camera ────────────────────────────────────────────────────
        const scene  = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(52, container.clientWidth / container.clientHeight, 0.1, 100);
        camera.position.set(0, 0.5, 7.5);
        camera.lookAt(0, 0, 0);

        // ── Lighting ──────────────────────────────────────────────────────────
        scene.add(new THREE.AmbientLight(0x0d0818, 0.7));

        const purpleLight = new THREE.PointLight(C_PURPLE, 3.5, 14);
        purpleLight.position.set(-3, 2, 3);
        scene.add(purpleLight);

        const blueLight = new THREE.PointLight(C_BLUE, 2.5, 12);
        blueLight.position.set(3, -1, 2);
        scene.add(blueLight);

        const tealLight = new THREE.PointLight(C_TEAL, 1.5, 10);
        tealLight.position.set(0, 3, -2);
        scene.add(tealLight);

        // ── Floating particles ────────────────────────────────────────────────
        const pCount = 70; // low count for memory efficiency
        const pGeo   = new THREE.BufferGeometry();
        const pPos   = new Float32Array(pCount * 3);
        const pColors = new Float32Array(pCount * 3);
        for (let i = 0; i < pCount; i++) {
            const theta = Math.random() * Math.PI * 2;
            const phi   = Math.acos(2 * Math.random() - 1);
            const r     = 2.2 + Math.random() * 2.5;
            pPos[i * 3]     = r * Math.sin(phi) * Math.cos(theta);
            pPos[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
            pPos[i * 3 + 2] = r * Math.cos(phi);
            // Mix purple and blue colors
            const t = Math.random();
            pColors[i * 3]     = 0.51 * (1 - t) + 0.17 * t;   // R
            pColors[i * 3 + 1] = 0.35 * (1 - t) + 0.52 * t;   // G
            pColors[i * 3 + 2] = 0.78 * (1 - t) + 0.79 * t;   // B
        }
        pGeo.setAttribute('position', new THREE.BufferAttribute(pPos, 3));
        pGeo.setAttribute('color',    new THREE.BufferAttribute(pColors, 3));
        const pMat = new THREE.PointsMaterial({
            size: 0.028, transparent: true, opacity: 1.0,
            sizeAttenuation: true, vertexColors: true,
        });
        const particles = new THREE.Points(pGeo, pMat);
        scene.add(particles);

        // ── Click effects pool ────────────────────────────────────────────────
        const clickEffects = [];

        function spawnClickEffect(worldPos) {
            if (clickEffects.length >= 3) return; // cap concurrent effects
            const duration = 1.0;
            const meshes   = [];

            // ── 1. Thin shockwave ring — white ───────────────────────────────
            const r1Geo = new THREE.TorusGeometry(0.14, 0.006, 8, 80);
            const r1Mat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.7 });
            const r1    = new THREE.Mesh(r1Geo, r1Mat);
            r1.position.copy(worldPos);
            r1.lookAt(camera.position);
            scene.add(r1);
            meshes.push({ mesh: r1, mat: r1Mat, type: 'wave1' });

            // ── 2. Slower, larger outer ring — dimmer white ──────────────────
            const r2Geo = new THREE.TorusGeometry(0.14, 0.003, 8, 80);
            const r2Mat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.35 });
            const r2    = new THREE.Mesh(r2Geo, r2Mat);
            r2.position.copy(worldPos);
            r2.lookAt(camera.position);
            scene.add(r2);
            meshes.push({ mesh: r2, mat: r2Mat, type: 'wave2' });

            // ── 3. Dot burst — 10 small white particles ──────────────────────
            for (let i = 0; i < 10; i++) {
                const dGeo  = new THREE.SphereGeometry(0.018, 5, 5);
                const dMat  = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.9 });
                const dot   = new THREE.Mesh(dGeo, dMat);
                dot.position.copy(worldPos);
                const angle = (i / 10) * Math.PI * 2;
                const speed = 1.4 + Math.random() * 1.0;
                dot.userData.vel = new THREE.Vector3(
                    Math.cos(angle) * speed,
                    Math.sin(angle) * speed,
                    (Math.random() - 0.5) * 0.3
                );
                scene.add(dot);
                meshes.push({ mesh: dot, mat: dMat, type: 'dot' });
            }

            clickEffects.push({
                meshes,
                life: 0,
                duration,
                update(dt) {
                    this.life += dt;
                    const t    = Math.min(this.life / this.duration, 1);
                    const ease = 1 - Math.pow(1 - t, 3); // ease-out cubic

                    meshes.forEach(({ mesh, mat, type }) => {
                        if (type === 'wave1') {
                            const s = 1 + ease * 22;
                            mesh.scale.set(s, s, s);
                            mat.opacity = (1 - t) * 0.7;
                        } else if (type === 'wave2') {
                            const s = 1 + ease * 34;
                            mesh.scale.set(s, s, s);
                            mat.opacity = (1 - t) * 0.3;
                        } else if (type === 'dot') {
                            mesh.position.addScaledVector(mesh.userData.vel, dt);
                            mesh.userData.vel.multiplyScalar(0.84);
                            mat.opacity = Math.max(0, 0.9 - t * 1.4);
                            const ss = Math.max(0.01, 1 - t * 0.9);
                            mesh.scale.set(ss, ss, ss);
                        }
                    });

                    return t >= 1;
                },
                dispose() {
                    meshes.forEach(({ mesh, mat }) => {
                        scene.remove(mesh);
                        mesh.geometry.dispose();
                        mat.dispose();
                    });
                },
            });
        }

        // ── Click → world position via Raycaster ──────────────────────────────
        const heroEl = heroRef.current;
        const onClick = (e) => {
            if (!renderer) return;
            try {
                const rect = renderer.domElement.getBoundingClientRect();
                if (rect.width === 0 || rect.height === 0) return;
                const ndcX =  ((e.clientX - rect.left) / rect.width)  * 2 - 1;
                const ndcY = -((e.clientY - rect.top)  / rect.height) * 2 + 1;

                // Cast ray into scene and intersect with z=0 plane (scene centre)
                const ray = new THREE.Raycaster();
                ray.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);
                const plane    = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
                const worldPos = new THREE.Vector3();
                const hit      = ray.ray.intersectPlane(plane, worldPos);
                if (!hit) return; // ray parallel to plane — skip

                // Clamp to keep effect inside the visible scene
                if (worldPos.length() > 4.5) worldPos.setLength(4.5);
                spawnClickEffect(worldPos);
            } catch (_) { /* ignore */ }
        };
        if (heroEl) heroEl.addEventListener('click', onClick);

        // ── Mouse parallax ────────────────────────────────────────────────────
        let mx = 0, my = 0;
        const onMove = (e) => {
            mx = (e.clientX / window.innerWidth  - 0.5) * 0.6;
            my = (e.clientY / window.innerHeight - 0.5) * 0.6;
        };
        window.addEventListener('mousemove', onMove);

        // ── Pause when tab hidden or hero scrolled out of view ────────────────
        let heroPaused = false;
        const onHeroVisChange = () => { heroPaused = document.hidden; };
        document.addEventListener('visibilitychange', onHeroVisChange);

        const heroObserver = new IntersectionObserver(
            ([entry]) => { heroPaused = !entry.isIntersecting || document.hidden; },
            { threshold: 0.05 }
        );
        heroObserver.observe(container);

        // ── Animation loop (throttled ~30fps) ─────────────────────────────────
        let animId;
        const clock = new THREE.Clock();
        const HERO_FRAME_MS = 1000 / 24; // 24fps — lighter on CPU/GPU
        let heroLastTime = 0;

        const animate = (ts) => {
            animId = requestAnimationFrame(animate);
            if (heroPaused) return;
            if (ts - heroLastTime < HERO_FRAME_MS) return;
            heroLastTime = ts;
            const dt = clock.getDelta();
            const elapsed = clock.elapsedTime;

            // Particles slow drift
            particles.rotation.y += 0.0005;
            particles.rotation.x += 0.0002;

            // ── Click effects update & cleanup ────────────────────────────────
            for (let i = clickEffects.length - 1; i >= 0; i--) {
                const done = clickEffects[i].update(dt);
                if (done) {
                    clickEffects[i].dispose();
                    clickEffects.splice(i, 1);
                }
            }

            // Mouse parallax on camera
            camera.position.x += (mx - camera.position.x) * 0.03;
            camera.position.y += (0.5 - my - camera.position.y) * 0.03;
            camera.lookAt(0, 0, 0);

            renderer.render(scene, camera);
        };
        animate();

        // ── Resize ────────────────────────────────────────────────────────────
        const onResize = () => {
            camera.aspect = container.clientWidth / container.clientHeight;
            camera.updateProjectionMatrix();
            renderer.setSize(container.clientWidth, container.clientHeight);
        };
        window.addEventListener('resize', onResize);

        // ── Cleanup ───────────────────────────────────────────────────────────
        return () => {
            cancelAnimationFrame(animId);
            document.removeEventListener('visibilitychange', onHeroVisChange);
            heroObserver.disconnect();
            if (heroEl) heroEl.removeEventListener('click', onClick);
            clickEffects.forEach(fx => fx.dispose());
            clickEffects.length = 0;
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('resize', onResize);
            if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
            renderer.dispose();
            scene.traverse(obj => {
                if (obj.geometry) obj.geometry.dispose();
                if (obj.material) {
                    if (Array.isArray(obj.material)) obj.material.forEach(m => m.dispose());
                    else obj.material.dispose();
                }
            });
        };
    }, []);

    // ── Small white model canvas above hero text ───────────────────────────────
    useEffect(() => {
        const container = modelRef.current;
        if (!container) return;
        const testCanvas = document.createElement('canvas');
        if (!(testCanvas.getContext('webgl') || testCanvas.getContext('experimental-webgl'))) return;

        let rdr;
        try { rdr = new THREE.WebGLRenderer({ alpha: true, antialias: true }); }
        catch (e) { return; }

        const S = container.clientWidth || 140;
        rdr.setSize(S, S);
        rdr.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        rdr.setClearColor(0x000000, 0);
        container.appendChild(rdr.domElement);

        const sc  = new THREE.Scene();
        const cam = new THREE.PerspectiveCamera(42, 1, 0.1, 100);
        cam.position.set(0, 0, 4);

        sc.add(new THREE.AmbientLight(0xffffff, 1.4));
        const d1 = new THREE.DirectionalLight(0xffffff, 2.0);
        d1.position.set(2, 3, 4); sc.add(d1);
        const d2 = new THREE.DirectionalLight(0xccddff, 0.7);
        d2.position.set(-2, -1, 2); sc.add(d2);

        let root = null;
        new GLTFLoader().load('/assets/models/hero-model.glb', (gltf) => {
            const box    = new THREE.Box3().setFromObject(gltf.scene);
            const center = box.getCenter(new THREE.Vector3());
            const size   = box.getSize(new THREE.Vector3());
            gltf.scene.position.sub(center);
            gltf.scene.scale.setScalar(2.8 / Math.max(size.x, size.y, size.z));
            gltf.scene.traverse(child => {
                if (child.isMesh)
                    child.material = new THREE.MeshStandardMaterial({
                        color: 0xffffff, emissive: 0xffffff,
                        emissiveIntensity: 0.10, metalness: 0.2, roughness: 0.4,
                    });
            });
            root = gltf.scene;
            sc.add(root);
        });

        let id;
        const clk = new THREE.Clock();
        const loop = () => {
            id = requestAnimationFrame(loop);
            if (root) {
                root.rotation.y += 0.007;
                root.position.y  = Math.sin(clk.getElapsedTime() * 1.1) * 0.07;
            }
            rdr.render(sc, cam);
        };
        loop();

        return () => {
            cancelAnimationFrame(id);
            if (container.contains(rdr.domElement)) container.removeChild(rdr.domElement);
            rdr.dispose();
        };
    }, []);

    return (
        <>
            <div ref={heroRef} className="hero-style13" style={{ position: 'relative', overflow: 'hidden', paddingBottom: '200px', cursor: 'crosshair' }}>
                {/* 3D Background */}
                <div
                    ref={mountRef}
                    style={{
                        position: 'absolute',
                        top: 0, left: 0,
                        width: '100%', height: '100%',
                        zIndex: 0,
                        pointerEvents: 'none',
                    }}
                />

                <div className="container" style={{ position: 'relative', zIndex: 1 }}>
                    <div className="hero-content13" style={{ textAlign: 'center' }}>

                        {/* White 3D model logo above title */}
                        <div ref={modelRef} style={{ width: 210, height: 210, margin: '0 auto 20px' }} />

                        <h1 className="title" style={{
                            color: 'white',
                            textAlign: 'center',
                            fontFamily: isRtl ? "'Tajawal', Arial, sans-serif" : "'DM Sans', Arial, sans-serif",
                            fontWeight: isRtl ? 900 : 800,
                            fontSize: 'clamp(2.2rem, 5.5vw, 4.2rem)',
                            lineHeight: isRtl ? 1.45 : 1.1,
                            letterSpacing: isRtl ? 'normal' : '-1.5px',
                            direction: isRtl ? 'rtl' : 'ltr',
                            marginBottom: '28px',
                        }}>
                            {t("Transform Your Business WIZLY")}
                        </h1>

                        <div
                            className="description-container"
                            style={{
                                minHeight: '80px',
                                marginBottom: '48px',
                                color: 'rgba(255,255,255,0.72)',
                                fontSize: '1.1rem',
                                lineHeight: '1.7',
                                direction: isRtl ? 'rtl' : 'ltr',
                                textAlign: 'center',
                                display: 'flex',
                                justifyContent: 'center',
                                maxWidth: '650px',
                                margin: '0 auto 48px',
                            }}
                        >
                            <Typewriter
                                key={i18n.language}
                                options={{
                                    strings: [t("WIZ is a privately owned Palestinian company specialized in IT & AI solution.At WIZ, we are facilitating other's life and make it easier to achieve their business & personal goals and desires.")],
                                    autoStart: true,
                                    loop: true,
                                    delay: 40,
                                    cursor: '|',
                                }}
                            />
                        </div>

                        {/* ── CTA buttons ── */}
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '18px', flexWrap: 'wrap' }}>
                            {/* Primary — Contact Us */}
                            <Link to={getPath('/contact')} className="wiz-btn-primary">
                                {t("Let's Build Together")}
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '8px', verticalAlign: 'middle' }}>
                                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                                </svg>
                            </Link>
                            {/* Secondary — scroll to Our Work in Action */}
                            <a
                                href="#work-in-action"
                                className="wiz-btn-outline"
                                onClick={(e) => {
                                    e.preventDefault();
                                    document.getElementById('work-in-action')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }}
                            >
                                {t("See Our Work")}
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '8px', verticalAlign: 'middle' }}>
                                    <polyline points="6 9 12 15 18 9"/>
                                </svg>
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <style dangerouslySetInnerHTML={{ __html: `
                .Typewriter__wrapper { color: white !important; }
                .Typewriter__cursor  { color: #8258c8 !important; }

                /* ── Primary button — purple→blue gradient ── */
                .wiz-btn-primary {
                    display: inline-flex;
                    align-items: center;
                    padding: 15px 36px;
                    border-radius: 50px;
                    background: linear-gradient(102deg, #8258c8 0%, #2c84c8 100%);
                    color: #ffffff !important;
                    font-size: 15px;
                    font-weight: 700;
                    letter-spacing: 0.4px;
                    text-decoration: none;
                    border: none;
                    box-shadow: 0 8px 28px rgba(130,88,200,0.35), 0 0 0 0 rgba(130,88,200,0.3);
                    transition: all 0.32s cubic-bezier(0.25,0.46,0.45,0.94);
                }
                .wiz-btn-primary:hover {
                    transform: translateY(-3px) scale(1.03);
                    box-shadow: 0 0 36px rgba(130,88,200,0.5), 0 12px 36px rgba(44,132,200,0.3);
                    color: #ffffff !important;
                }

                /* ── Outline button — teal accent ghost ── */
                .wiz-btn-outline {
                    display: inline-flex;
                    align-items: center;
                    padding: 14px 34px;
                    border-radius: 50px;
                    background: rgba(255,255,255,0.05);
                    backdrop-filter: blur(14px);
                    -webkit-backdrop-filter: blur(14px);
                    color: #ffffff !important;
                    font-size: 15px;
                    font-weight: 600;
                    letter-spacing: 0.4px;
                    text-decoration: none;
                    border: 1.5px solid rgba(130,88,200,0.45);
                    box-shadow: 0 4px 20px rgba(0,0,0,0.2), inset 0 0 0 0 rgba(130,88,200,0);
                    transition: all 0.32s cubic-bezier(0.25,0.46,0.45,0.94);
                }
                .wiz-btn-outline:hover {
                    transform: translateY(-3px) scale(1.03);
                    background: rgba(130,88,200,0.14);
                    border-color: rgba(130,88,200,0.7);
                    box-shadow: 0 0 28px rgba(130,88,200,0.25), 0 8px 28px rgba(0,0,0,0.25);
                    color: #ffffff !important;
                }
                [dir="rtl"] .wiz-btn-primary,
                [dir="rtl"] .wiz-btn-outline { letter-spacing: normal; }
            `}} />
        </>
    );
};

export default Hero;
